import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { JobReadinessDto } from '@ups/xplat/api/dto';
import { JobReadinessService } from '@ups/xplat/api/services';

import {
  jobReadinessCheckLabel,
  jobReadinessTooltip,
  jobReadinessVisual,
  sortChecksBySeverity,
} from './job-readiness-indicator.utils';

/**
 * Job-readiness pre-flight indicator (WI 30856). Drop-in, job-scoped traffic
 * light: pass `[jobId]` (the Sparta Job GUID) and it fetches the readiness
 * result and renders a red/green/warn icon whose click opens a details dialog
 * naming the exact setup gaps. Renders nothing when no job is selected or the
 * readiness could not be read, so it is safe to place next to any job dropdown.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ups-job-readiness-indicator',
  standalone: true,
  imports: [CommonModule, ButtonModule, DialogsModule, TooltipModule],
  templateUrl: './job-readiness-indicator.component.html',
  styleUrls: ['./job-readiness-indicator.component.scss'],
})
export class JobReadinessIndicatorComponent {
  private readonly service = inject(JobReadinessService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jobId$ = new Subject<string>();

  private _jobId: string;
  /** Sparta Job GUID. Setting it (re)loads readiness; a falsy value clears the indicator. */
  @Input()
  set jobId(value: string) {
    this._jobId = value;
    if (value) {
      this.loading.set(true);
      this.errored.set(false);
      this.jobId$.next(value);
    } else {
      this.reset();
    }
  }
  get jobId(): string {
    return this._jobId;
  }

  readonly readiness = signal<JobReadinessDto | null>(null);
  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly dialogOpen = signal(false);

  readonly visual = computed(() => {
    const dto = this.readiness();
    return dto ? jobReadinessVisual(dto.OverallStatus) : null;
  });
  readonly tooltip = computed(() => {
    const dto = this.readiness();
    return dto ? jobReadinessTooltip(dto.OverallStatus) : '';
  });
  /** Dialog rows, severity-sorted, with label and icon resolved here so the template calls nothing. */
  readonly checkRows = computed(() =>
    sortChecksBySeverity(this.readiness()?.Checks).map((check) => ({
      check,
      label: jobReadinessCheckLabel(check.CheckKey),
      visual: jobReadinessVisual(check.Status),
    }))
  );

  constructor() {
    this.jobId$
      .pipe(
        // catchError lives INSIDE the switchMap projection so a failed request
        // (e.g. a routine 404 for a temp / not-linked-to-Viewpoint job) is
        // isolated to that request and never terminates the outer stream. If it
        // reached the outer pipe, the subscription would die and every later job
        // selection would set loading=true with no subscriber — a permanent
        // stuck spinner for the rest of the session.
        switchMap((id) =>
          this.service.getJobReadiness(id).pipe(
            map((dto) => ({ dto, error: false as const })),
            catchError(() => of({ dto: null, error: true as const }))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (result.error) {
          // Fail quiet: an out-of-scope / unlinked job (404) or a transient
          // error should not disrupt the host page — just hide the indicator.
          this.reset();
          this.errored.set(true);
        } else {
          this.readiness.set(result.dto);
          this.loading.set(false);
          this.errored.set(false);
        }
      });
  }

  openDialog(): void {
    if (this.readiness()) {
      this.dialogOpen.set(true);
    }
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
  }

  private reset(): void {
    this.readiness.set(null);
    this.loading.set(false);
    this.dialogOpen.set(false);
  }
}
